import { useEffect, useState } from "react";
import { api } from "../api";
import PriceTable from "./PriceTable";
import AffiliationVatPanel from "./AffiliationVatPanel";
import { G_DARK, Badge, Btn, Card, Collapsible, Field, Input, Select, Textarea, Spinner }
  from "../components/Shared";
import { ROLE_LABELS, ROLE_COLORS, grantsOf, isResearcher, managedGroupIds, roleNeedsGroup }
  from "../auth/roles";
import { TagsPanel, ProjectFieldsPanel } from "./GroupCatalogPanels";
import DocumentsPanel from "./DocumentsPanel";
import RetentionPanel from "./RetentionPanel";
import BackupCard from "../components/BackupCard";
import { BILLING_LINES } from "./AccountCard";
import { useDocumentWord } from "../documentWord";

// The facility's own details, and its prices. Separate from the admin-only
// "Groups" tab, which creates and deletes groups: this edits ONE group, and is
// a group admin's job rather than an admin's.

// The facility's address is the same six lines, labels and order as an
// account's billing identity (`BILLING_LINES`) — imported, not copied, so the
// two labellings of one address cannot drift apart again. This map is the one
// place the account's keys meet the group's columns: the frontend mirror of
// `services/accounts.py::BILLING_FROM_GROUP`.
//
// Gone from the card on 2026-09-29: *Short code* (`groups.code`, which silently
// overrode the quotation prefix) and the *Billing address* box, which nothing
// ever read. "Cost centre" went on 2026-09-16 for the same reason — a field
// that saves and reaches nothing is a control that looks like it works.
const GROUP_COLUMN_OF = {
  billing_name:    "legal_name",
  billing_street:  "address_street",
  billing_zip:     "address_zip",
  billing_city:    "address_city",
  billing_country: "address_country",
  vat_id:          "vat_id",
};

const ADDRESS = BILLING_LINES.map(([key, label]) => {
  // A line added to BILLING_LINES with no column here would render an input
  // that saves into a key the server ignores. Refuse loudly instead.
  if (!GROUP_COLUMN_OF[key]) throw new Error(`No group column for billing line ${key}`);
  return [GROUP_COLUMN_OF[key], label];
});

const CONTACT = [
  ["website", "Website"],
  ["contact_person", "Contact person"],
  ["contact_email", "Contact email"],
  ["contact_phone", "Phone"],
];

const FACILITY_KEYS = [...ADDRESS, ...CONTACT].map(([k]) => k);

const DAY_COUNTS = ["quote_valid_days", "order_days", "project_days"];

const GRID = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 };
const SECTION = { margin: "0 0 10px", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5, color: "#6b7280" };

/** One group's profile, loaded for a card, and a save that PATCHes only the
 *  payload that card built. Each card loads its own copy: saving one cannot
 *  send the other's unsaved edits, which is the point of having two. */
function useGroupProfile(groupId) {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setProfile(null);
    setSaved(false);
    api.get(`/api/groups/${groupId}/profile`).then(setProfile).catch(e => setError(e.message));
  }, [groupId]);

  const set = k => e => { setProfile(p => ({ ...p, [k]: e.target.value })); setSaved(false); };

  const save = async payload => {
    setBusy(true); setError(null);
    try {
      setProfile(await api.patch(`/api/groups/${groupId}/profile`, payload));
      setSaved(true);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return { profile, busy, saved, error, set, save };
}

function SaveRow({ canEdit, busy, saved, onSave, label, blocked }) {
  if (!canEdit) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
      <Btn onClick={onSave} disabled={busy || !!blocked}>{busy ? <><Spinner /> Saving…</> : label}</Btn>
      {blocked && <span role="status" style={{ color: "#b91c1c", fontSize: 13 }}>{blocked}</span>}
      {saved && !blocked && <span style={{ color: G_DARK, fontSize: 13 }}>Saved.</span>}
    </div>
  );
}

function FacilityDetailsForm({ groupId, canEdit }) {
  const word = useDocumentWord();
  const { profile, busy, saved, error, set, save } = useGroupProfile(groupId);

  if (error) return <div role="alert" style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div>;
  if (!profile) return <div style={{ color: "#6b7280" }}><Spinner /> Loading…</div>;

  const onSave = () => {
    const payload = {};
    for (const k of FACILITY_KEYS) payload[k] = profile[k] ?? "";
    return save(payload);
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>Facility details</h3>
      <p style={{ margin: "0 0 16px", color: "#6b7280", fontSize: 13 }}>
        Who the facility is and where it sits. The address reads exactly like an
        account's billing identity, and is what a {word()} prints under <em>From</em>.
      </p>
      {[["Address", ADDRESS], ["Contact", CONTACT]].map(([section, items]) => (
        <div key={section} style={{ marginBottom: 20 }}>
          <h4 style={SECTION}>{section}</h4>
          <div data-form-grid style={GRID}>
            {items.map(([key, label]) => (
              <Field key={key} label={label}>
                <Input value={profile[key] ?? ""} onChange={set(key)} disabled={!canEdit} />
              </Field>
            ))}
          </div>
        </div>
      ))}
      <SaveRow canEdit={canEdit} busy={busy} saved={saved} onSave={onSave} label="Save details" />
    </Card>
  );
}

function QuotationSettingsForm({ groupId, canEdit }) {
  const { profile, busy, saved, error, set, save } = useGroupProfile(groupId);
  // The settings of the documents this facility issues, named for the current
  // *Customer documents* setting (2026-11-03).
  const word = useDocumentWord();

  if (error) return <div role="alert" style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div>;
  if (!profile) return <div style={{ color: "#6b7280" }}><Spinner /> Loading…</div>;

  // Required (2026-09-29): an empty prefix is what the boot's copy of the
  // retired short code fills, so a cleared one came back as the old code at
  // the next restart. The server refuses it too; this says so before the save.
  const prefixMissing = !(profile.quote_number_prefix ?? "").trim()
    ? `A ${word()} number prefix is required — ${word({ plural: true })} are numbered from it.`
    : null;

  const onSave = () => {
    if (prefixMissing) return undefined;
    const payload = {
      quote_number_prefix: (profile.quote_number_prefix ?? "").trim(),
      default_currency: profile.default_currency || "EUR",
      billing_email: profile.billing_email ?? "",
      quote_terms: profile.quote_terms ?? "",
      sow_note: profile.sow_note ?? "",
    };
    // Empty means "unset", which is a real answer for each — not zero.
    for (const k of DAY_COUNTS) {
      payload[k] = profile[k] === "" || profile[k] == null ? null : Number(profile[k]);
    }
    return save(payload);
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>{word({ plural: true, capital: true })} &amp; billing</h3>
      <p style={{ margin: "0 0 16px", color: "#6b7280", fontSize: 13 }}>
        {word.sow
          ? "How this facility issues Statements of Work. The note is frozen into each one when it is issued, so changing it later cannot alter what somebody already agreed to."
          : <>How this facility quotes. The terms are frozen into each quotation when it
        is issued, so changing them later cannot alter what somebody already agreed to.</>}
      </p>
      <div data-form-grid style={GRID}>
        <Field label={`${word({ capital: true })} number prefix`} required
               hint={`e.g. BINF — ${word({ plural: true })} are numbered from it. At most 16 characters, and no other group may use it.`}>
          <Input value={profile.quote_number_prefix ?? ""} onChange={set("quote_number_prefix")}
                 maxLength={16} disabled={!canEdit} />
        </Field>
        <Field label="Default currency">
          <Input value={profile.default_currency ?? "EUR"} onChange={set("default_currency")} disabled={!canEdit} />
        </Field>
      </div>
      {/* Several, separated by commas: a facility bills an office, and an office
          is routinely two addresses. The server refuses the whole update and
          names the entry that is wrong, so a typo in the second one cannot be
          saved. "Invoicing addresses" until 2026-09-16: what arrives here is the
          record a bill is written FROM, and the invoice is issued elsewhere. */}
      <div data-form-grid style={{ ...GRID, marginTop: 14 }}>
        <Field label="Billing recipients"
               hint="Where the record of finished work is sent, for the bill to be issued from. Several, separated by commas or semicolons.">
          <Input value={profile.billing_email ?? ""} onChange={set("billing_email")} disabled={!canEdit} />
        </Field>
      </div>
      {/* One number each for the whole facility, and none belongs on a single
          quotation: a deadline that closes work should not be whatever the last
          person filling in a form happened to type.
          A quotation nobody signs in time still archives its project — nothing
          was agreed — while the work deadline closes the ORDER, and the project
          goes only once no live order is left. All three apply to projects
          created after they are changed: a number is captured when a project is
          made, so editing it here leaves running work exactly as it was. */}
      <div data-form-grid style={{ ...GRID, marginTop: 14 }}>
        <Field label={`${word({ capital: true })} valid for (days)`}
               hint={`How long there is to sign it. 30 if blank. A ${word()} nobody signs in time archives its project. Applies to projects created from now on.`}>
          <Input type="number" min="1" max="730" value={profile.quote_valid_days ?? ""}
                 onChange={set("quote_valid_days")} disabled={!canEdit} />
        </Field>
        {/* **Two different periods, and the labels have to keep them apart.**
            "Quotation valid for" is how long there is to SIGN it; this one is
            how long the WORK has once it is signed. They were briefly both
            called "Quotation …" on 2026-09-16, during the sweep that took the
            word *order* out of every screen — which removed the word and the
            distinction with it. The distinction is the point; only the word had
            to go. */}
        <Field label="Work deadline (days)"
               hint={`How long the work has once everybody has signed — counted from the signature, not from when the ${word()} was written. Blank means no deadline. Work that runs late is closed; the project goes only when no live ${word()} is left. Applies to projects created from now on.`}>
          <Input type="number" min="1" max="730" value={profile.order_days ?? ""}
                 onChange={set("order_days")} disabled={!canEdit} />
        </Field>
        <Field label="Project deadline (days)"
               hint="How long a project runs, from the day it is created. Blank means no deadline. It WAITS: a project whose time is up but which still has an open order is left alone, so this closes projects nobody is using. Applies to projects created from now on.">
          <Input type="number" min="1" max="3650" value={profile.project_days ?? ""}
                 onChange={set("project_days")} disabled={!canEdit} />
        </Field>
      </div>
      {/* A Statement of Work prints the note below instead of these, so while
          the portal issues them the field says what it is still for: a priced
          document, which a facility may issue again after switching back. */}
      <Field label={word.sow ? "Terms (priced documents only)" : "Quotation terms"}
             hint={word.sow
               ? "Stamped onto a priced document and frozen into it when signed. A Statement of Work prints the note below in their place."
               : "Stamped onto every quotation and frozen into the signed document"}
             style={{ marginTop: 14 }}>
        <Textarea rows={5} value={profile.quote_terms ?? ""} onChange={set("quote_terms")} disabled={!canEdit} />
      </Field>
      {/* 2026-10-28: when the portal issues Statements of Work, the customer's
          document carries no prices and no terms; this note stands where a
          quotation prints its terms. */}
      <Field label="Statement of Work note"
             hint={word.sow
               ? "Printed on a Statement of Work in place of terms — e.g. that charges are invoiced separately. Empty prints nothing."
               : "Printed on a Statement of Work where a quotation prints its terms — e.g. that charges are invoiced separately. Empty prints nothing."}
             style={{ marginTop: 14 }}>
        <Textarea rows={3} value={profile.sow_note ?? ""} onChange={set("sow_note")} disabled={!canEdit} />
      </Field>
      <SaveRow canEdit={canEdit} busy={busy} saved={saved} onSave={onSave}
               label={`Save ${word()} settings`} blocked={prefixMissing} />
    </Card>
  );
}

function PriceLists({ groupId, canEdit }) {
  const [lists, setLists] = useState(null);
  const [preview, setPreview] = useState(null);
  const [missing, setMissing] = useState(null);
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => api.get(`/api/groups/${groupId}/price-lists`).then(setLists).catch(e => setError(e.message));
  // `load` is redefined every render; listing it would refetch on
  // every keystroke elsewhere in the component.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLists(null); setPreview(null); load(); }, [groupId]);

  const onFile = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null); setBusy(true); setPreview(null); setMissing(null);
    try {
      const text = await file.text();
      setContent(text);
      // Dry run first, always. The same endpoint does both, so what is shown
      // here is what committing will do.
      setPreview(await api.post(`/api/groups/${groupId}/price-lists/import`,
                                { content: text, commit: false }));
    } catch (err) {
      // A refusal for unknown names is not a dead end: it carries the names, so
      // they can be reviewed and created here. Without this the import could
      // never be completed on a group that had no services yet.
      // Services only. Affiliations are no longer refusable — the server reports
      // the ones it would create in the preview body instead — and leaving the
      // old condition here would route an empty `unknown_affiliations: []` into
      // a panel with nothing in it.
      const d = err.detail || err.data;
      if (d && d.unknown_services?.length) {
        setMissing(d);
      } else {
        setError(err.message);
      }
    } finally { setBusy(false); }
  };

  const createMissing = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/groups/${groupId}/services`, {
        services: missing.unknown_services.map(u => ({
          name: u.name, category: u.category, ppms_system_name: u.ppms_system_name })),
      });
      setMissing(null);
      setPreview(await api.post(`/api/groups/${groupId}/price-lists/import`,
                                { content, commit: false }));
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/groups/${groupId}/price-lists/import`, { content, commit: true });
      setPreview(null); setContent(null);
      await load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/groups/${groupId}/price-lists/${id}/status?status=${status}`);
      await load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Price lists</h3>
      {error && <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>{error}</div>}

      {canEdit && (
        <div style={{ marginBottom: 18 }}>
          <Field label="Import a PPMS price export (.csv)"
                 hint="Nothing is written until you confirm. Affiliations named by the file are created with it; an unknown SERVICE stops the whole import rather than skipping a row.">
            <input type="file" accept=".csv,text/csv" onChange={onFile} disabled={busy} />
          </Field>
        </div>
      )}

      {missing && (
        <div style={{ marginBottom: 18, padding: "14px 16px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.35)" }}>
          <strong style={{ fontSize: 14 }}>Nothing was imported yet</strong>
          <p style={{ margin: "6px 0 10px", fontSize: 13, color: "#92400e" }}>
            {missing.message}
          </p>
          {missing.unknown_services?.length > 0 && (
            <>
              <p style={{ margin: "0 0 6px", fontSize: 13 }}>
                These {missing.unknown_services.length} service(s) are in the file but not in this
                group. Read them before adding — a typo would become a service of its own.
              </p>
              <div style={{ maxHeight: 160, overflowY: "auto", fontSize: 12, marginBottom: 10 }}>
                {missing.unknown_services.map(u => (
                  <div key={u.name}>{u.name}{u.category ? ` · ${u.category}` : ""}</div>
                ))}
              </div>
              {canEdit && (
                <Btn onClick={createMissing} disabled={busy}>
                  {busy ? <><Spinner /> Adding…</> : `Add ${missing.unknown_services.length} service(s) and retry`}
                </Btn>
              )}
            </>
          )}
        </div>
      )}

      {preview && (
        <div style={{ marginBottom: 18, padding: "14px 16px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
          <strong style={{ fontSize: 14 }}>This import would write {preview.counts.total} rule(s)</strong>
          <ul style={{ margin: "8px 0 12px", paddingLeft: 18, fontSize: 13, color: "#374151" }}>
            <li>{preview.counts.new} new</li>
            <li>{preview.counts.changed} with a different price</li>
            <li>{preview.counts.unchanged} unchanged</li>
          </ul>
          {preview.new_affiliations?.length > 0 && (
            <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 6,
                          background: "rgba(217,119,6,0.07)", border: "1px solid rgba(217,119,6,0.3)" }}>
              <p style={{ margin: "0 0 6px", fontSize: 13 }}>
                It would also create <strong>{preview.new_affiliations.length} affiliation(s)</strong> this
                group does not have yet, named by the file. Read them first — an unexpected one is
                usually a typo, and the name is a starting value you can edit afterwards.
              </p>
              <div style={{ maxHeight: 160, overflowY: "auto", fontSize: 12 }}>
                {preview.new_affiliations.map(a => (
                  <div key={a.code}><code>{a.code}</code> → {a.label}</div>
                ))}
              </div>
            </div>
          )}
          {preview.changed.length > 0 && (
            <div data-table-scroll style={{ maxHeight: 200, overflowY: "auto", marginBottom: 12 }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                <tbody>
                  {preview.changed.map((c, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "4px 8px" }}>{c.service}</td>
                      <td style={{ padding: "4px 8px", color: "#6b7280" }}>{c.affiliation}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>{c.was} → <strong>{c.now}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <Btn onClick={commit} disabled={busy}>{busy ? <><Spinner /> Importing…</> : "Import as draft"}</Btn>
            <Btn variant="secondary" onClick={() => { setPreview(null); setContent(null); }}>Cancel</Btn>
          </div>
        </div>
      )}

      {lists === null ? <div style={{ color: "#6b7280" }}><Spinner /> Loading…</div>
        : lists.length === 0 ? <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>No price lists yet.</p>
        : (
        <table className="responsive-table" style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
              <th style={{ padding: "8px 10px" }}>Label</th>
              <th style={{ padding: "8px 10px" }}>Status</th>
              <th style={{ padding: "8px 10px" }}>Rules</th>
              <th style={{ padding: "8px 10px" }}>Valid</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lists.map(pl => (
              <tr key={pl.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td data-label="Label" style={{ padding: "8px 10px" }}>{pl.label}</td>
                <td data-label="Status" style={{ padding: "8px 10px" }}>{pl.status}</td>
                <td data-label="Rules" style={{ padding: "8px 10px" }}>{pl.rules}</td>
                <td data-label="Valid" style={{ padding: "8px 10px", color: "#6b7280" }}>
                  {pl.valid_from || "—"} → {pl.valid_to || "—"}
                </td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>
                  {canEdit && pl.status === "draft" && (
                    <Btn variant="secondary" onClick={() => setStatus(pl.id, "approved")} disabled={busy}
                         style={{ padding: "4px 10px", fontSize: 12 }}>Approve</Btn>
                  )}
                  {canEdit && pl.status === "approved" && (
                    <Btn onClick={() => setStatus(pl.id, "published")} disabled={busy}
                         style={{ padding: "4px 10px", fontSize: 12 }}>Publish</Btn>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function TeamPanel({ groupId, groupName }) {
  /* Who is in this group, and as what.
   *
   * Read-only on purpose. Accounts are created and edited under Users, one card
   * up — putting a second editor here would mean two screens that must agree
   * about grants, and the reason `sync_role_cache` has exactly one writer is
   * that two sources of truth about a role is how the group_admin role silently
   * lost access in five places.
   *
   * What this answers is the question Users cannot: "who is in MY group", for
   * somebody who administers one facility and does not want the institution's
   * whole account list.
   *
   * **Staff only, since 2026-09-28** (*users are no longer associated with
   * groups, only with projects*). A `user` grant holds no group, and one still
   * filed in a group is the old shape on its way out — so the list is the
   * accounts holding a staff role HERE, and the researchers are pointed to
   * through the group's projects, counted off the `projects` that
   * `GET /api/admin/users` names for each account.
   */
  const [members, setMembers] = useState(null);
  const [researchers, setResearchers] = useState(0);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!groupId) return;
    let live = true;
    setMembers(null);
    setError(null);
    api.get("/api/admin/users")
      .then(all => {
        if (!live) return;
        setMembers(all.filter(u => staffGrantsHere(u, groupId).length));
        setResearchers(all.filter(u => isResearcher(u)
          && (u.projects || []).some(p => p.group_id === groupId)).length);
      })
      // A group admin may call /api/admin/users; if that ever changes, say so
      // rather than showing an empty group, which reads as "nobody is in it".
      .catch(e => live && setError(e.message || "Could not load this group's members"));
    return () => { live = false; };
  }, [groupId]);

  if (error) {
    return <p style={{ margin: 0, fontSize: 13, color: "#b91c1c" }}>{error}</p>;
  }
  if (members === null) return <Spinner />;
  const name = groupName || "this group";
  // Where the researchers are, said whether or not there is any staff yet.
  const pointer = (
    <p style={{ margin: "6px 0 0", fontSize: 12, color: "#6b7280" }}>
      Researchers belong to projects, not to a group:{" "}
      {researchers === 1 ? "1 researcher works" : `${researchers} researchers work`} on{" "}
      {groupName ? `${groupName}'s` : "this group's"} projects. Each project lists its own
      members, owner and representative under Projects.
    </p>
  );
  if (!members.length) {
    return (
      <div>
        <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
          Nobody holds a staff role in {name} yet. Accounts are created under Users.
        </p>
        {pointer}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {members.map(u => (
        <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                                 fontSize: 13 }}>
          <strong>{u.full_name || u.email}</strong>
          <span style={{ color: "#6b7280" }}>{u.email || "—"}</span>
          <span style={{ flex: 1 }} />
          {staffGrantsHere(u, groupId)
            .map(g => (
              <Badge key={g.id ?? g.role} color={ROLE_COLORS[g.role]}>
                {ROLE_LABELS[g.role] || g.role}
              </Badge>
            ))}
        </div>
      ))}
      <p style={{ margin: "6px 0 0", fontSize: 12, color: "#6b7280" }}>
        Roles are granted and withdrawn under Users.
      </p>
      {pointer}
    </div>
  );
}

// The grants that make an account staff of `groupId`: operator or group admin
// held here. A `user` grant is not one, even in the old shape that files it in
// a group.
function staffGrantsHere(u, groupId) {
  return grantsOf(u).filter(g => g.group_id === groupId && roleNeedsGroup(g.role));
}


/** One group's settings.
 *
 * `role` is the role of the CARD this is rendered in, handed down by
 * `renderSection` — not the strongest grant the account holds. The two are
 * different for every dual-role account, which on dev is both staff accounts,
 * and until 2026-09-15 this component asked the second question twice:
 * `isGlobalAdmin(me)` chose the group list, and `isGlobalAdmin(me) || …` chose
 * whether they could be edited. So the group-admin card of an admin listed
 * every group in the facility and offered to save changes to all of them —
 * a card widening what a role can do, which is the one thing
 * `auth/managementSections.js` forbids.
 *
 * Narrowing the `groups` prop alone does not fix that: the second question was
 * still answered from the account. A component has to be TOLD which card it is
 * in.
 */
export default function GroupManagementTab({ me, groups, role = "group_admin" }) {
  const word = useDocumentWord();
  // The narrower role is the default on purpose: a caller that forgets to say
  // which card this is gets the group admin's reach, not the admin's.
  const cardIsAdmin = role === "admin";
  // Still a question about `me`, and deliberately so — it is a different one.
  // "Does this account administer THIS group" is per-group and the card cannot
  // answer it; "is this account an admin somewhere" is what may not be asked.
  const administered = managedGroupIds(me, "group_admin");
  const mine = cardIsAdmin ? groups : groups.filter(g => administered.includes(g.id));
  const [groupId, setGroupId] = useState(mine[0]?.id ?? null);

  if (mine.length === 0) {
    return (
      <Card>
        <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>Facility settings</h3>
        <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
          You do not administer a group. Group details and price lists are managed by a
          group admin.
        </p>
      </Card>
    );
  }

  // An admin may hold no group of their own, so they pick; a group admin of one
  // group has nothing to pick from and is not asked.
  const canEdit = cardIsAdmin || administered.includes(groupId);

  return (
    <>
      {mine.length > 1 && (
        <div style={{ marginBottom: 16, maxWidth: 320 }}>
          <Field label="Group">
            <Select
              options={mine.map(g => ({ value: String(g.id), label: g.name }))}
              value={String(groupId ?? "")}
              onChange={e => setGroupId(Number(e.target.value))}
            />
          </Field>
        </div>
      )}
      {/* Named sub-cards rather than one flat column of panels.
          Eight, not the five the task named — the other four are settings that
          were already here and had to land somewhere, and a card named after
          each is more honest than folding Retention into "Storage" because the
          list said five. Nothing was dropped: every panel below appears exactly
          once, which is the check that mattered. */}
      <Collapsible title="Facility details" level={1} defaultOpen
                   subtitle="who the facility is: name, address, VAT id, contact">
        <FacilityDetailsForm groupId={groupId} canEdit={canEdit} />
      </Collapsible>

      {/* Two literals rather than the word: `test_management_section_names.py` reads
          the card names off this file, and a message may point at either. */}
      <Collapsible title={word.sow ? "Statements of Work & billing" : "Quotations & billing"} level={1}
                   subtitle="numbering, currency, billing recipients, deadlines, terms">
        <QuotationSettingsForm groupId={groupId} canEdit={canEdit} />
      </Collapsible>

      <Collapsible title="Team management" level={1}
                   subtitle="who is in this group, and as what">
        <TeamPanel groupId={groupId} groupName={mine.find(g => g.id === groupId)?.name} />
      </Collapsible>

      {/* No "Storage management" card since 2026-11-03: storage is one
          setting for the whole portal, on the admin's Portal settings, and a
          project keeps the bucket it was created in whatever group it is in. */}
      <Collapsible title="Project management" level={1}
                   subtitle="tags and the fields a project carries">
        <TagsPanel groupId={groupId} canEdit={canEdit} />
        <div style={{ height: 20 }} />
        <ProjectFieldsPanel groupId={groupId} canEdit={canEdit} />
      </Collapsible>

      {/* "Policy documents", not "Documents". The name is used for four things
          otherwise — a group's policies, the project card's own list, this
          settings card, and the PDFs a project can download — and the two a
          group admin meets in one session are this one and the project's. */}
      <Collapsible title="Policy documents" level={1}
                   subtitle="what this facility asks people to confirm">
        <DocumentsPanel groupId={groupId} canEdit={canEdit} />
      </Collapsible>

      {/* Three panels stacked flat, NOT two more Collapsibles inside this one.
          Pricing was the only place in Management that went four deep — role,
          section, sub-card, then Prices/VAT — and a reviewer's complaint that
          the section could not be browsed pointed here. Nothing is hidden by
          the change: both panels keep their heading, and they now read at the
          same level as Price lists above them, which is what they always were.
          "Project management" already stacks two panels exactly this way. */}
      <Collapsible title="Pricing" level={1}
                   subtitle="price lists, every line item, and the VAT each kind of customer pays">
        <PriceLists groupId={groupId} canEdit={canEdit} />

        <div style={{ height: 24 }} />
        {/* PriceTable and AffiliationVatPanel render no heading of their own —
            the Collapsible titles were their only labels, so removing the
            collapse without adding these would leave two unnamed tables. */}
        <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Prices</h3>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
          Every line item, filterable.
        </p>
        <PriceTable groupId={groupId} />

        <div style={{ height: 24 }} />
        <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>VAT</h3>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
          Per kind of customer, not one rate for all.
        </p>
        <AffiliationVatPanel groupId={groupId} canEdit={canEdit} />
      </Collapsible>

      {/* Deliberately admin-only to CHANGE, visible to a group admin: they are
          the ones who would ask for it, and they should see what it would take
          before anybody turns it on. Its own card rather than folded into
          Storage — one number here deletes every file of every archived
          project, and that does not belong behind a heading about buckets. */}
      <Collapsible title="Retention" level={1}
                   subtitle="how long archived projects are kept">
        {/* The card's role again, not the account's: an admin who is also a
            group admin may set this in the Admin card and reads it in the
            other one, which is what "visible to a group admin" meant. */}
        <RetentionPanel groupId={groupId} canEdit={cardIsAdmin} />
      </Collapsible>

      <Collapsible title="Backup" level={1}
                   subtitle="this group's rows, as SQL">
        <BackupCard scope="group" groupId={groupId}
                    groupName={mine.find(g => g.id === groupId)?.name} />
      </Collapsible>
    </>
  );
}
